import type { Cell, PackRequest, PackResult, PlacedPiece } from "./types";
import { buildHighsLP } from "./highsModel";
import { solveLp } from "./highsWorker";

/** Solve a 2D-packing instance with HiGHS, returning placements + value. */
export async function solvePacking(req: PackRequest): Promise<PackResult> {
  const { cols, rows, items, timeLimitSec = 30 } = req;

  const t0 = performance.now();
  const { lp, placements } = buildHighsLP(items, cols, rows);
  const tBuildMs = performance.now() - t0;

  // tSolveMs is measured inside the worker around highs.solve() only, so it
  // never includes the one-time WASM init or the wait behind queued solves.
  const { sol, solveMs: tSolveMs } = await solveLp(lp, { time_limit: timeLimitSec, presolve: "on" });

  const out: PlacedPiece[] = [];
  let value = 0, cellsUsed = 0;
  if (sol.Status === "Optimal" || sol.Status === "Feasible") {
    for (let p = 0; p < placements.length; p++) {
      const col = sol.Columns["x" + p];
      if (col && col.Primal > 0.5) {
        const pl = placements[p];
        const minR = Math.min(...pl.cells.map((c) => c[0]));
        const minC = Math.min(...pl.cells.map((c) => c[1]));
        out.push({
          itemIndex: pl.itemIdx,
          key: items[pl.itemIdx].key,
          value: pl.value,
          r: minR,
          c: minC,
          cells: pl.cells.map(([r, c]): Cell => [r - minR, c - minC]),
        });
        value += pl.value;
        cellsUsed += pl.cells.length;
      }
    }
  }

  const status =
    sol.Status === "Optimal" ? "OPTIMAL" :
    sol.Status === "Feasible" ? "FEASIBLE" :
    String(sol.Status).toUpperCase();

  return {
    status,
    value: Math.round(sol.ObjectiveValue ?? value),
    cellsUsed,
    placements: out,
    tBuildMs,
    tSolveMs,
  };
}
